import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { AlertCircle, Lightbulb, Heart, CheckCircle2, MessageSquare, Trash2, Loader2, Filter, Eye, Send, Reply } from "lucide-react";

const KIND_META = {
  complaint: { label: "Reclamação", color: "#EF4444", bg: "#FEE2E2", icon: AlertCircle },
  suggestion: { label: "Sugestão", color: "#F59E0B", bg: "#FEF3C7", icon: Lightbulb },
  praise: { label: "Elogio", color: "#10B981", bg: "#D1FAE5", icon: Heart },
};

export default function GymFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [q, setQ] = useState("");
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const openReply = (fb) => {
    setReplyingId(fb.id);
    setReplyText(fb.admin_reply?.message || "");
  };
  const submitReply = async (fb) => {
    if (!replyText.trim() || replyText.trim().length < 2) return toast.error("Escreva sua resposta");
    setSendingReply(true);
    try {
      await api.post(`/gym-feedback/${fb.id}/reply`, { message: replyText.trim() });
      toast.success("✅ Resposta enviada — o aluno recebeu uma notificação");
      setReplyingId(null); setReplyText("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
    finally { setSendingReply(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gym-feedback");
      setItems(data);
    } catch (e) { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (fb) => {
    if (fb.read) return;
    try { await api.patch(`/gym-feedback/${fb.id}`, { read: true }); load(); } catch {}
  };
  const toggleResolved = async (fb) => {
    try { await api.patch(`/gym-feedback/${fb.id}`, { resolved: !fb.resolved, read: true }); toast.success(!fb.resolved ? "Marcado como resolvido" : "Reaberto"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir este feedback?")) return;
    try { await api.delete(`/gym-feedback/${id}`); load(); toast.success("Excluído"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const filtered = items.filter((x) => {
    if (filterKind !== "all" && x.kind !== filterKind) return false;
    if (filterStatus === "unread" && x.read) return false;
    if (filterStatus === "open" && x.resolved) return false;
    if (filterStatus === "resolved" && !x.resolved) return false;
    if (q && !(x.message?.toLowerCase().includes(q.toLowerCase()) || x.student_name?.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const counters = items.reduce((acc, x) => {
    acc.total++;
    acc[x.kind] = (acc[x.kind] || 0) + 1;
    if (!x.read) acc.unread++;
    if (!x.resolved) acc.open++;
    return acc;
  }, { total: 0, unread: 0, open: 0 });

  return (
    <div className="space-y-4">
      <PageHeader title="Feedback dos Alunos" subtitle="Reclamações, sugestões e elogios enviados pelo portal do aluno"/>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: counters.total, color: "#0f172a", bg: "#f1f5f9" },
          { label: "Não lidas", value: counters.unread, color: "#E11D48", bg: "#FEE2E2" },
          { label: "Reclamações", value: counters.complaint || 0, color: KIND_META.complaint.color, bg: KIND_META.complaint.bg },
          { label: "Sugestões", value: counters.suggestion || 0, color: KIND_META.suggestion.color, bg: KIND_META.suggestion.bg },
          { label: "Elogios", value: counters.praise || 0, color: KIND_META.praise.color, bg: KIND_META.praise.bg },
        ].map((k) => (
          <Card key={k.label} className="p-3 border-slate-200" style={{ background: k.bg }}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">{k.label}</div>
            <div className="text-2xl font-heading font-black" style={{ color: k.color }}>{k.value}</div>
          </Card>
        ))}
      </div>

      <Card className="p-3 border-slate-200 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-xs text-slate-500"><Filter className="w-3 h-3"/>Filtrar</div>
        <select value={filterKind} onChange={(e)=>setFilterKind(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1" data-testid="feedback-filter-kind">
          <option value="all">Todos tipos</option>
          <option value="complaint">Reclamações</option>
          <option value="suggestion">Sugestões</option>
          <option value="praise">Elogios</option>
        </select>
        <select value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1" data-testid="feedback-filter-status">
          <option value="all">Todos status</option>
          <option value="unread">Não lidas</option>
          <option value="open">Em aberto</option>
          <option value="resolved">Resolvidos</option>
        </select>
        <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por texto ou nome..." className="flex-1 min-w-[200px]" data-testid="feedback-search"/>
      </Card>

      {loading ? (
        <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin"/></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-500 border-slate-200">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-2"/>
          Nenhum feedback encontrado.
        </Card>
      ) : (
        <div className="space-y-3" data-testid="feedback-list">
          {filtered.map((fb) => {
            const meta = KIND_META[fb.kind] || KIND_META.suggestion;
            const Icon = meta.icon;
            return (
              <Card key={fb.id} className={`p-4 border-slate-200 ${!fb.read ? "ring-2 ring-rose-200" : ""}`} data-testid={`feedback-${fb.id}`} onClick={() => markRead(fb)}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                    <Icon className="w-5 h-5" style={{ color: meta.color }}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                      <span className="font-bold text-sm text-slate-900">{fb.student_name || "Aluno"}</span>
                      {fb.resolved && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/>Resolvido</span>}
                      {!fb.read && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500 text-white font-bold uppercase">Novo</span>}
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap mb-2">{fb.message}</p>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-2">
                      {new Date(fb.created_at).toLocaleString("pt-BR")}
                    </div>
                    {fb.admin_reply && (
                      <div className="my-2 p-3 rounded-xl bg-slate-50 border-l-4 border-emerald-400" data-testid={`feedback-reply-preview-${fb.id}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Reply className="w-3 h-3 text-emerald-600"/>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Sua resposta · {fb.admin_reply.author_name}</span>
                          <span className="text-[10px] text-slate-400">{new Date(fb.admin_reply.created_at).toLocaleDateString("pt-BR")}</span>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{fb.admin_reply.message}</p>
                      </div>
                    )}
                    {replyingId === fb.id && (
                      <div className="my-2 p-3 rounded-xl bg-emerald-50 border-2 border-emerald-200" onClick={(e)=>e.stopPropagation()}>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Escreva sua resposta ao aluno</div>
                        <textarea value={replyText} onChange={(e)=>setReplyText(e.target.value)} rows={3} maxLength={2000}
                          placeholder="Olá! Agradecemos seu contato..."
                          className="w-full border border-emerald-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-emerald-400 resize-none"
                          data-testid={`feedback-reply-input-${fb.id}`}/>
                        <div className="flex items-center gap-2 mt-2">
                          <Button size="sm" onClick={()=>submitReply(fb)} disabled={sendingReply} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`feedback-reply-send-${fb.id}`}>
                            {sendingReply ? <><Loader2 className="w-3 h-3 mr-1 animate-spin"/>Enviando</> : <><Send className="w-3 h-3 mr-1"/>Enviar resposta</>}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={()=>{setReplyingId(null); setReplyText("");}}>Cancelar</Button>
                          <span className="text-[10px] text-slate-400 ml-auto">O aluno receberá uma notificação push</span>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                      {replyingId !== fb.id && (
                        <Button size="sm" onClick={(e)=>{e.stopPropagation(); openReply(fb);}} className="text-xs bg-sky-600 hover:bg-sky-700 text-white" data-testid={`feedback-reply-${fb.id}`}>
                          <Reply className="w-3 h-3 mr-1"/>{fb.admin_reply ? "Editar resposta" : "Responder pelo app"}
                        </Button>
                      )}
                      <Button size="sm" variant={fb.resolved ? "outline" : "default"} onClick={(e)=>{e.stopPropagation(); toggleResolved(fb);}} className={`text-xs ${!fb.resolved ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`} data-testid={`feedback-resolve-${fb.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1"/>{fb.resolved ? "Reabrir" : "Marcar resolvido"}
                      </Button>
                      {fb.student_phone && (
                        <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">📱 {fb.student_phone}</span>
                      )}
                      <Button size="sm" variant="ghost" onClick={(e)=>{e.stopPropagation(); remove(fb.id);}} className="text-xs text-rose-500 hover:bg-rose-50 ml-auto" data-testid={`feedback-delete-${fb.id}`}>
                        <Trash2 className="w-3 h-3"/>
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
